import { useState, useEffect, useRef, useMemo } from "react";
import Select from "@rio-cloud/rio-uikit/Select";
import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import SearchAutocomplete from "../../form/searchAutocomplete";
import { Dealership } from "../../../models/dealership";
import { fetchDealerships } from "../../../api/fetchDealerships";
import { ButtonDropdown, Multiselect } from "@rio-cloud/rio-uikit";
import TypeButtonGroup from "./typeButtonGroup";
import NewOccurrenceButton from "./newOccurrenceButton";
import { Occurrence } from "../../../models/occurrence";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  getAllCells,
  getAllRegionals,
  getRegionalsByCel,
  getCellsByRegional,
} from "../../../data/cellRegionalTraceability";

interface IFilterRowProps {
  searchByChassis: (searchTerm: string) => void;
  setCriticalityFilter: (criticality: string) => void;
  setStartDateFilter: ({
    startDate,
    endDate,
  }: {
    startDate?: string | undefined;
    endDate?: string | undefined;
  }) => void;
  setUpdateDateFilter: ({
    startDate,
    endDate,
  }: {
    startDate?: string | undefined;
    endDate?: string | undefined;
  }) => void;
  setTypeFilter: (type: string[]) => void;
  typeFilter: string[];
  setIsAlertFilter: (isAlert: string) => void;
  setFinishedFilter: (finished: string[]) => void;
  setDealershipFilter: (dealerships: string[]) => void;
  setRegionalFilter?: (regionals: string[]) => void;
  setCellFilter?: (cells: string[]) => void;
  persona: 'MANAGER' | 'TOWER' | 'CONSULTANT' | 'PARTNERSHIP';
  viewType: 'card' | 'list';
  setViewType: (viewType: 'card' | 'list') => void;
  finalized?: boolean;
  partners: { id: string; name: string }[];
  isValidStep: (requiredFields: string[], newOccurrence: Occurrence) => boolean;
  create?: (
    occurrence: Occurrence
  ) => Promise<{ wasCreated: boolean; error?: string }>;
  exportButton?: React.ReactNode;
  copyTokenButton?: React.ReactNode;
}

const FilterRow = (props: IFilterRowProps) => {
  const [chassis, setChassis] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [updateStartDate, setUpdateStartDate] = useState<string | undefined>(
    undefined
  );
  const [updateEndDate, setUpdateEndDate] = useState<string | undefined>(
    undefined
  );
  const [finishedFilter, setFinishedFilter] = useState<string[]>(['false']);
  const [dealershipsSelected, setDealershipsSelected] = useState<Dealership[]>([]);
  const [typeMaxWidth, setTypeMaxWidth] = useState<string>("100%");
  const typeContainerRef = useRef<HTMLDivElement>(null);
  const isResponsiveView = useMediaQuery('(max-width: 1024px)');
  const [showOpenDateDropdown, setShowOpenDateDropdown] = useState(false);
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [updateEndDateError, setUpdateEndDateError] = useState<string | null>(null);
  const [regional, setRegional] = useState<string[]>(['undefined']);
  const [celula, setCelula] = useState<string[]>(['undefined']);

  const validCellsForRequest = useMemo(() => {
    if (regional.length === 0 || (regional.length === 1 && regional[0] === 'undefined')) {
      return celula.filter((c) => c !== 'undefined');
    }
    
    const validCells = new Set<number>();
    regional.forEach((r: string) => {
      if (r !== 'undefined') {
        const cellsForRegional = getCellsByRegional(Number(r));
        cellsForRegional.forEach((c) => validCells.add(c));
      }
    });
    
    return celula.filter((c) => c === 'undefined' || validCells.has(Number(c)));
  }, [regional, celula]);

  const getAvailableRegionals = () => {
    return getAllRegionals();
  };

  const getAvailableCells = () => {
    if (regional.length === 0 || (regional.length === 1 && regional[0] === 'undefined')) {
      return getAllCells();
    }
    
    const validCells = new Set<number>();
    regional.forEach((r: string) => {
      if (r !== 'undefined') {
        const cellsForRegional = getCellsByRegional(Number(r));
        cellsForRegional.forEach((c) => validCells.add(c));
      }
    });
    
    return Array.from(validCells).sort((a, b) => a - b);
  };

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseYMD = (dateLocal?: string) => {
    if (!dateLocal) return undefined;
    const [year, month, day] = dateLocal.split('-').map(Number);
    if (!year || !month || !day) return undefined;
    return new Date(year, month - 1, day);
  };

  const applyPresetTo = (
    preset: string,
    target: "open" | "update"
  ) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let starts: string | undefined;
    let ends: string | undefined;

    switch (preset) {
      case "TODAY": {
        const start = new Date(today);
        const end = new Date(today);
        ends = formatDate(end);
        starts = formatDate(start);
        break;
      }

      case "LAST_24H": {
        const start = new Date(Date.now() - 24 * 60 * 60 * 1000);
        starts = formatDate(start);
        ends = formatDate(new Date());
        break;
      }

      case "YESTERDAY": {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        starts = formatDate(y);
        ends = formatDate(y);
        break;
      }

      case "LAST_3_DAYS": {
        const start = new Date(today);
        start.setDate(today.getDate() - 2);
        starts = formatDate(start);
        ends = formatDate(today);
        break;
      }

      case "LAST_10_DAYS": {
        const start = new Date(today);
        start.setDate(today.getDate() - 9);
        starts = formatDate(start);
        ends = formatDate(today);
        break;
      }

      case "CLEAR":
      default:
        starts = undefined;
        ends = undefined;
    }

    if (target === "open") {
      setStartDate(starts);
      setEndDate(ends);
      props.setStartDateFilter({ startDate: starts, endDate: ends });
    } else {
      setUpdateStartDate(starts);
      setUpdateEndDate(ends);
      props.setUpdateDateFilter({ startDate: starts, endDate: ends });
    }

    if (preset === "CLEAR") {
      setDatePickerKey(prev => prev + 1);
    }
    setShowOpenDateDropdown(false);
  };

  const openDateItems = (target: "open" | "update") => [
    {
      value: "Hoje",
      onSelect: () => applyPresetTo("TODAY", target)
    },
    {
      value: "Últimas 24 horas",
      onSelect: () => applyPresetTo("LAST_24H", target)
    },
    {
      value: "Ontem",
      onSelect: () => applyPresetTo("YESTERDAY", target)
    },
    {
      value: "Últimos 3 dias",
      onSelect: () => applyPresetTo("LAST_3_DAYS", target)
    },
    {
      value: "Últimos 10 dias",
      onSelect: () => applyPresetTo("LAST_10_DAYS", target)
    },
    { divider: true },
    {
      value: <span className="text-red-600">Limpar filtro</span>,
      onSelect: () => applyPresetTo("CLEAR", target)
    }
  ];


  // Update dealership filter when selection changes
  useEffect(() => {
    props.setDealershipFilter(dealershipsSelected.map(d => d.id));
  }, [dealershipsSelected]);

  useEffect(() => {
    if (props.setCellFilter) {
      const filtered = validCellsForRequest.filter((c) => c !== 'undefined');
      props.setCellFilter(filtered);
    }
  }, [validCellsForRequest]);

  const removeDealership = (dealershipId: string) => {
    setDealershipsSelected(prev => prev.filter(d => d.id !== dealershipId));
  };

  const addDealership = (dealership: Dealership) => {
    if (!dealershipsSelected.find(d => d.id === dealership.id)) {
      setDealershipsSelected(prev => [...prev, dealership]);
    }
  };

  // This is a hack to make the type filter width equal to the criticality filter width
  useEffect(() => {
    const updateMaxWidth = () => {
      const criticalityElement = document.getElementById('criticality-filter');
      if (criticalityElement && typeContainerRef.current) {
        const criticalityWidth = criticalityElement.offsetWidth;
        setTypeMaxWidth(`${criticalityWidth}px`);
      }
    };
    updateMaxWidth();
    window.addEventListener('resize', updateMaxWidth);
    return () => window.removeEventListener('resize', updateMaxWidth);
  }, []);

  return (
    <div
      className={`flex flex-col items-center justify-between w-full py-[15px] ${isResponsiveView ? 'flex-wrap mt-4' : ''
        }`}
    >
      <div
        className={`flex gap-4 items-end w-full flex-wrap`}
      >
        <div className="mb-6 flex-shrink-0">
          {props.persona !== 'MANAGER' &&
            props.persona !== 'PARTNERSHIP' &&
            !props.finalized && (
              <NewOccurrenceButton
                partners={props.partners}
                create={props.create ?? (async () => ({ wasCreated: false }))}
                isValidStep={(
                  requiredFields: string[],
                  newOccurrence: Occurrence
                ) => props.isValidStep(requiredFields, newOccurrence)}
                persona={props.persona}
              />
            )}
        </div>

        <div
          className={`form-group ${chassis?.length && chassis?.length !== 17
              ? 'has-feedback has-error'
              : ''
            } ${isResponsiveView ? 'min-w-full order-1' : 'flex-1 min-w-0'}`}
        >
          <label htmlFor="search">&nbsp;</label>
          <div className="input-group">
            <span className="input-group-addon">
              <span className="rioglyph rioglyph-search" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar o chamado pelo chassi"
              id="search"
              onChange={(e) => {
                setChassis(e.target.value);
                if (e.target.value.length !== 17 && e.target.value.length !== 0)
                  return;
                props.searchByChassis(e.target.value);
              }}
              maxLength={17}
            />
          </div>
        </div>

        <div
          className={`form-group ${isResponsiveView
              ? 'min-w-full order-2'
              : 'flex-1 min-w-0 max-w-[220px]'
            }`}
          id="criticality-filter"
        >
          <label htmlFor="criticality" className="truncate mb-[0] pb-[0]">
            Criticidade
          </label>
          <Select
            id="criticality"
            options={[
              { id: 'undefined', label: 'Todas' },
              { id: '1', label: 'Não urgente' },
              { id: '2', label: 'Pouco urgente' },
              { id: '3', label: 'Urgente' },
              { id: '4', label: 'Muito urgente' },
              { id: '5', label: 'Emergência' },
            ]}
            value={['undefined']}
            onChange={(e) => {
              props.setCriticalityFilter(e?.id ?? 'undefined');
            }}
          />
        </div>

        <div className={`form-group ${isResponsiveView
            ? 'min-w-full order-3'
            : 'flex-1 min-w-0 max-w-[200px]'
          }`}>
          <label htmlFor="regional" className="truncate mb-[0] pb-[0]">Regional</label>
          <div className="w-full">
            <Multiselect
              id="regional"
              inputAddon="rioglyph rioglyph-filter"
              options={[
                { id: 'undefined', label: 'Todas' },
                ...getAvailableRegionals().map((r) => ({ id: String(r), label: String(r) })),
              ]}
              value={regional}
              onChange={(e) => {
                if (!e || e.length === 0) {
                  setRegional(['undefined']);
                  if (props.setRegionalFilter) props.setRegionalFilter([]);
                  return;
                }
                
                if (e.includes('undefined') && !regional.includes('undefined')) {
                  setRegional(['undefined']);
                  if (props.setRegionalFilter) props.setRegionalFilter([]);
                  return;
                }
                
                const selected = e.filter((item: any) => item !== 'undefined');
                const toSet = selected.length ? selected : ['undefined'];
                setRegional(toSet);
                if (props.setRegionalFilter) props.setRegionalFilter(selected);
              }}
            />
          </div>
        </div>

        <div className={`form-group ${isResponsiveView
            ? 'min-w-full order-4'
            : 'flex-1 min-w-0 max-w-[200px]'
          }`}>
          <label htmlFor="celula" className="truncate mb-[0] pb-[0]">Célula</label>
          <div className="w-full">
            <Multiselect
              id="celula"
              inputAddon="rioglyph rioglyph-filter"
              options={[
                { id: 'undefined', label: 'Todas' },
                ...getAvailableCells().map((c) => ({ id: String(c), label: String(c) })),
              ]}
              value={celula}
              onChange={(e) => {
                if (!e || e.length === 0) {
                  setCelula(['undefined']);
                  if (props.setCellFilter) props.setCellFilter([]);
                  return;
                }
                
                if (e.includes('undefined') && !celula.includes('undefined')) {
                  setCelula(['undefined']);
                  if (props.setCellFilter) props.setCellFilter([]);
                  return;
                }
                
                const selected = e.filter((item: any) => item !== 'undefined');
                const toSet = selected.length ? selected : ['undefined'];
                setCelula(toSet);
                if (props.setCellFilter) props.setCellFilter(selected);
              }}
            />
          </div>
        </div>

      </div>

      <div className={`flex w-full mt-4 ${isResponsiveView ? 'flex-wrap gap-y-4' : ''}`}>
        <div className={`${isResponsiveView ? 'min-w-full' : 'flex-1 min-w-0'} form-group`} id="dealership-filter">
          <div className="w-full">
            <label htmlFor="dealerships" className="truncate mb-[0] pb-[0]">Concessionárias</label>
            <div className="w-full" style={{ width: '100%' }}>
              {/* Selected dealerships tags */}
              {dealershipsSelected.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {dealershipsSelected.map((dealership) => (
                    <span key={dealership.id} className="inline-flex items-center px-2 py-1 rounded-md text-xl font-medium bg-gray-100 text-gray-700 border border-gray-300">
                      {dealership.name}{dealership.city && `, ${dealership.city}`}{dealership.state && `-${dealership.state}`}, DN: {dealership.id}
                      <button type="button" className="ml-2 inline-flex items-center justify-center text-gray-700 hover:text-gray-900" onClick={() => removeDealership(dealership.id)}>
                        <span aria-hidden="true" className="text-2xl font-bold">×</span>
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <SearchAutocomplete
                id="dealershipSearch"
                placeholder="Buscar concessionária pelo nome ou DN..."
                fetchOptions={async (searchTerm: string) => await fetchDealerships(searchTerm, searchTerm)}
                optionLabel={{ id: 'DN', name: 'Nome' }}
                onSelectedOption={(dealership: Dealership) => addDealership(dealership)}
                overlay
                overlayClassName="!w-[15%]"
                onClear={() => { }}
                valueRenderer={(option: Dealership) => `${option.name}${option.city ? `, ${option.city}` : ''}${option.state ? `-${option.state}` : ''}, DN: ${option.id}`}
                optionRenderer={(option: Dealership) => {
                  const isSelected = dealershipsSelected.find(d => d.id === option.id);
                  return (
                    <div className={`truncate ${isSelected ? 'bg-gray-100 text-gray-700' : ''}`}>
                      {isSelected && <span className="mr-1">✓</span>}
                      {option.name}{option.city && `, ${option.city}`}{option.state && `-${option.state}`}, DN: {option.id}
                      {isSelected && <span className="ml-1 text-xs">(selecionada)</span>}
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </div>

        {!isResponsiveView ? (<div className="mx-1 h-6 text-center mt-[33px]" />) : null}

        <div className={`${isResponsiveView ? 'w-full flex flex-col gap-3' : 'flex-1 min-w-0 flex'}`}>
          <div className={`${isResponsiveView ? 'form-group min-w-full' : 'form-group w-1/2 pr-2'}`}>
            <label htmlFor="type" className="truncate mb-[0] pb-[0]">Tipo</label>
            <div className="w-full">
              <Multiselect
                id="type"
                inputAddon="rioglyph rioglyph-filter"
                options={[
                  { id: 'MAINTENANCE_TYPE', label: 'Tipo de manutenção', header: true },
                  { id: 'PREVENTIVE', label: 'Preventiva' },
                  { id: 'CORRECTIVE', label: 'Corretiva' },
                  { id: 'HAS_CAMPAIGNS', label: 'Campanhas de Campo' },
                  { id: 'OCCURRENCE_TYPE', label: 'Tipo de ocorrência', header: true },
                  { id: 'ASSISTANCE', label: 'Socorro mecânico' },
                  { id: 'WINCH', label: 'Guincho' },
                  { id: 'IN_DEALERSHIP', label: 'Na concessionária' },
                ]}
                value={props.typeFilter}
                onChange={(e) => props.setTypeFilter(e ? e.filter((item: any) => item !== 'undefined') : [])}
              />
            </div>
          </div>

          <div className={`form-group min-w-[210px] ${isResponsiveView ? 'min-w-full' : ''}`} id="criticality-filter">
            <label htmlFor="isAlert" className="truncate mb-[0] pb-[0]">
              Em alerta
            </label>
            <Select
              id="isAlert"
              options={[
                { id: 'undefined', label: 'Todas' },
                { id: 'true', label: 'Sim' },
                { id: 'false', label: 'Não' },
              ]}
              value={['undefined']}
              onChange={(e) => {
                props.setIsAlertFilter(e?.id ?? 'undefined');
              }}
            />
          </div>

          <div className={`${isResponsiveView ? 'form-group min-w-full' : 'form-group w-1/2 pl-2'}`}>
            <label htmlFor="finished" className="truncate mb-[0] pb-[0]">Status</label>
            <div className="w-full">
              <Multiselect
                id="finished"
                inputAddon="rioglyph rioglyph-filter"
                options={[{ id: 'false', label: 'Em aberto' }, { id: 'true', label: 'Concluídos' }]}
                value={finishedFilter}
                onChange={(e) => {
                  const selectedValues = e ? e.filter((item: any) => item !== 'undefined') : [];
                  setFinishedFilter(selectedValues);
                  props.setFinishedFilter(selectedValues);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={`flex w-full mt-4 ${isResponsiveView ? 'flex-wrap gap-y-4' : 'items-start'}`}>
        <div className={`flex gap-3 items-center ${isResponsiveView ? 'w-full' : 'flex-1 min-w-0'}`}>
          <div className="form-group w-auto">
            <div className="relative">
              <ButtonDropdown
                className="mt-3"
                onOpen={() => setShowOpenDateDropdown(!showOpenDateDropdown)}
                onClose={() => setShowOpenDateDropdown(false)}
                title={<span className="rioglyph rioglyph-settings" aria-hidden="true" />}
                iconOnly
                items={openDateItems("open")}
              >
              </ButtonDropdown>
            </div>
          </div>
          <div className={`form-group w-full min-w-auto ${!startDate && endDate ? 'has-feedback has-error' : ''}`}>
            <label htmlFor="openDateStart" className="truncate mb-[0] pb-[0]">Data de abertura</label>
            <DatePicker
              key={`start-date-${datePickerKey}`}
              id="openDateStart"
              value={startDate ? parseYMD(startDate) : undefined}
              onChange={(e: any) => {
                if (typeof e === 'string' && e.length) return;
                if (typeof e === 'string') {
                  setStartDate(undefined);
                  props.setStartDateFilter({ startDate: undefined, endDate });
                  return;
                }
                const newStartDate = e?.toISOString('YYYY-MM-DD')?.split('T')[0];
                if (endDate && newStartDate) {
                  const startDateObj = parseYMD(newStartDate);
                  const endDateObj = parseYMD(endDate);
                  if (startDateObj && endDateObj && startDateObj > endDateObj) {
                    setDatePickerKey(prev => prev + 1);
                    return;
                  }
                }
                setStartDate(newStartDate);
                props.setStartDateFilter({ startDate: newStartDate, endDate });
              }}
              locale="pt-BR"
              dateFormat="DD-MM-YYYY"
              timeFormat={false}
              mandatory={false}
              clearableInput={true}
            />
          </div>

          <div><span className="text-[#697A94]">Até</span></div>

          <div className={`form-group w-full ${startDate && !endDate ? "has-feedback has-error" : ""} ${endDateError ? "has-feedback has-error" : ""}`}>
            <label htmlFor="openDateEnd" className="truncate mb-[0] pb-[0]">&nbsp;</label>
            <DatePicker
              key={`end-date-${datePickerKey}`}
              id="openDateEnd"
              value={endDate ? parseYMD(endDate) : undefined}
              onChange={(e: any) => {
                if (typeof e === 'string' && e.length) return;
                if (typeof e === 'string') {
                  setEndDate(undefined);
                  setEndDateError(null);
                  props.setStartDateFilter({ startDate, endDate: undefined });
                  return;
                }
                const newEndDate = e?.toISOString('YYYY-MM-DD')?.split('T')[0];
                if (startDate && newEndDate) {
                  const startDateObj = parseYMD(startDate);
                  const endDateObj = parseYMD(newEndDate);
                  if (startDateObj && endDateObj && endDateObj < startDateObj) {
                    setEndDateError('A data final não pode ser anterior à data inicial');
                    setDatePickerKey(prev => prev + 1);
                    setTimeout(() => setEndDateError(null), 3000);
                    return;
                  }
                }
                setEndDateError(null);
                setEndDate(newEndDate);
                props.setStartDateFilter({ startDate, endDate: newEndDate });
              }}
              locale="pt-BR"
              dateFormat="DD-MM-YYYY"
              timeFormat={false}
              mandatory={false}
              clearableInput={true}
            />
            {endDateError && (<span className="help-block text-red-600 text-sm">{endDateError}</span>)}
          </div>
        </div>

        {!isResponsiveView ? (<div className="border border-left-only border-width-2 mx-5 h-6 text-center mt-[33px]" />) : null}

        <div className={`flex gap-3 items-center ${isResponsiveView ? 'w-full' : 'flex-1 min-w-0'}`}>
          <div className="form-group w-auto">
            <div className="relative">
              <ButtonDropdown
                className="mt-3"
                onOpen={() => setShowOpenDateDropdown(!showOpenDateDropdown)}
                onClose={() => setShowOpenDateDropdown(false)}
                title={<span className="rioglyph rioglyph-settings" aria-hidden="true" />}
                iconOnly
                items={openDateItems("update")}
              />
            </div>
          </div>
          <div className={`form-group w-full ${!updateStartDate && updateEndDate ? 'has-feedback has-error' : ''}`}>
            <label htmlFor="updateDateStart" className="truncate mb-[0] pb-[0]">Data de atualização</label>
            <DatePicker
              key={`update-start-date-${datePickerKey}`}
              id="updateDateStart"
              value={updateStartDate ? parseYMD(updateStartDate) : undefined}
              onChange={(e: any) => {
                if (typeof e === 'string' && e.length) return;
                if (typeof e === 'string') {
                  setUpdateStartDate(undefined);
                  props.setUpdateDateFilter({ startDate: undefined, endDate: updateEndDate });
                  return;
                }
                const newStartDate = e?.toISOString('YYYY-MM-DD')?.split('T')[0];
                if (updateEndDate && newStartDate) {
                  const startDateObj = parseYMD(newStartDate);
                  const endDateObj = parseYMD(updateEndDate);
                  if (startDateObj && endDateObj && startDateObj > endDateObj) {
                    setDatePickerKey(prev => prev + 1);
                    return;
                  }
                }
                setUpdateStartDate(newStartDate);
                props.setUpdateDateFilter({ startDate: newStartDate, endDate: updateEndDate });
              }}
              locale="pt-BR"
              dateFormat="DD-MM-YYYY"
              timeFormat={false}
              mandatory={false}
              clearableInput={true}
            />
          </div>

          <div><span className="text-[#697A94]">Até</span></div>

          <div className={`form-group w-full ${updateStartDate && !updateEndDate ? "has-feedback has-error" : ""} ${updateEndDateError ? "has-feedback has-error" : ""}`}>
            <label htmlFor="updateDateEnd" className="truncate mb-[0] pb-[0]">&nbsp;</label>
            <DatePicker
              key={`update-end-date-${datePickerKey}`}
              id="updateDateEnd"
              value={updateEndDate ? parseYMD(updateEndDate) : undefined}
              onChange={(e: any) => {
                if (typeof e === 'string' && e.length) return;
                if (typeof e === 'string') {
                  setUpdateEndDate(undefined);
                  setUpdateEndDateError(null);
                  props.setUpdateDateFilter({ startDate: updateStartDate, endDate: undefined });
                  return;
                }
                const newEndDate = e?.toISOString('YYYY-MM-DD')?.split('T')[0];
                if (updateStartDate && newEndDate) {
                  const startDateObj = parseYMD(updateStartDate);
                  const endDateObj = parseYMD(newEndDate);
                  if (startDateObj && endDateObj && endDateObj < startDateObj) {
                    setUpdateEndDateError('A data final não pode ser anterior à data inicial');
                    setDatePickerKey(prev => prev + 1);
                    setTimeout(() => setUpdateEndDateError(null), 3000);
                    return;
                  }
                }
                setUpdateEndDateError(null);
                setUpdateEndDate(newEndDate);
                props.setUpdateDateFilter({ startDate: updateStartDate, endDate: newEndDate });
              }}
              locale="pt-BR"
              dateFormat="DD-MM-YYYY"
              timeFormat={false}
              mandatory={false}
              clearableInput={true}
            />
            {updateEndDateError && (<span className="help-block text-red-600 text-sm">{updateEndDateError}</span>)}
          </div>
        </div>

        {!isResponsiveView ? (<div className="border border-left-only border-width-2 mx-5 h-6 text-center mt-[33px]" />) : null}
        <div className="mt-[17px] ml-[-15px]">
          <TypeButtonGroup viewType={props.viewType} setViewType={props.setViewType} exportButton={props.exportButton} copyTokenButton={props.copyTokenButton} />
        </div>
      </div>
    </div>
  );
};

export default FilterRow;
